// src/data/deskQueue.js
//
// THE ORDERING BEHIND THE OFFICER'S DESK. Pure functions, no React and no Firestore, so the logic
// that decides WHAT NEEDS ATTENTION FIRST can be executed by a test suite rather than eyeballed in
// a browser. src/pages/LCDesk.js renders what this returns and adds nothing to it.
//
// This split is deliberate and follows the pattern the repo already uses: scripts\test-claim-payload
// .mjs proves src\data\claimPayload.js, not the page that calls it. A queue whose order is only ever
// checked by looking at a screen is a queue nobody has checked.
//
// THE RULE THAT GOVERNS EVERY FUNCTION HERE (PD-4): an unknown is never rendered as a zero and never
// as a safe value. A claim with no computable filing deadline does not sort as "plenty of time" and
// does not sort as "urgent" - it sorts LAST among the things waiting, and it is listed separately so
// the absence is visible rather than blank.

import { claimFiledAtSeconds } from './claimPayload';
import { LC_ROSTER } from './lcRoster';

// The point at which a filing window becomes the most important thing on an officer's screen.
// TEN DAYS IS THE ASSISTANT'S CHOICE AND NOT BOB'S RULING. It is the figure the 2026-08-28 weekend
// plan proposed and it has never been put to him. It lives here as one constant so overturning it
// is a one-line edit rather than a hunt.
export const WINDOW_URGENT_DAYS = 10;

// Stages at which a claim is sitting on the Local Chairperson's own desk, waiting for him to act.
export const AWAITING_LC = ['submitted', 'intake'];

// The stage a claim sits at once a Local Chairperson has sent it up. `gc_review` is the last rung
// LC_SETTABLE contains (claimLadder.js), so a claim resting here is one that left his desk upward.
//
// THE LIMIT STILL STANDS AND THE PAGE STILL SAYS IT. Batch 4 built the STAGE; it did not build an
// elevation EVENT. There is no `elevatedBy` and no `elevatedAt` anywhere in the schema, so this is a
// reading of WHERE a claim is, not a record of WHO moved it or WHEN. A GC can also set gc_review.
// Naming the stage truthfully is not the same as attributing the move, and the desk must not blur
// the two.
export const ELEVATED_LC = ['gc_review'];

// Stages past gc_review - the matter has moved beyond the General Chairperson's inbox as well.
export const PAST_LC = ['valuation', 'negotiation', 'settlement', 'closure', 'archived'];

// A RETURNED CLAIM - ruling 72, which amends 61.6.
//
// DEFINED HERE AND NOT IN claimReturn.js, ON PURPOSE. claimReturn.js already imports daysUntilISO
// from this module; if this module imported isReturned from that one the two would form a cycle.
// One definition, in the module with no dependency on the other, re-exported there so every existing
// import keeps working.
//
// A plain draft has never left its author and is NOT returned. The stamp is what distinguishes them.
export function isReturned(c) {
  return !!c && String(c.status || '') === 'draft' && !!c.returnedAt;
}

// Whole days from today to an ISO yyyy-mm-dd deadline. Negative means the date has passed; null means
// there is no deadline on record. `expiresAt` is written by computeExpiresAt() ONLY where the
// agreement carries a filing-window profile AND a date of occurrence exists, so null is common and
// must stay null. Same shape as the helper in src\pages\Claims.js, on purpose.
export function daysUntilISO(iso, now) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(String(iso))) return null;
  const d = new Date(String(iso) + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  const today = now ? new Date(now) : new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

// Whole days since a Firestore timestamp in seconds. null when unknown - an unknown age is not an
// age of zero.
export function daysSince(seconds, nowMs) {
  if (!seconds) return null;
  const now = typeof nowMs === 'number' ? nowMs : Date.now();
  return Math.floor((now / 1000 - seconds) / 86400);
}

export function isUnrouted(c) {
  return !!c && (c.routingStatus === 'unrouted' || String(c.scopeKey || '').endsWith('|UNROUTED'));
}

// VACANT ONLY, exactly as src\pages\Claims.js has it, and the distinction is the whole point:
//   VACANT         nobody sits in the seat, so no account holds the scopeKey and no human sees the
//                  claim. That is a routing hole.
//   NOT OF RECORD  the platform does not record WHO holds it (ruling 54 placeholder). Routing depends
//                  on an account carrying the scopeKey, not on the roster printing a name, so an
//                  attestation gap is not a routing hole.
// Folding NOT OF RECORD in here would mark 10 of 11 seats and make the signal useless on the one
// seat that is genuinely empty.
export function isUnheldSeat(c, roster) {
  const list = roster || LC_ROSTER;
  const sk = String(c && c.scopeKey ? c.scopeKey : '');
  if (!sk || sk.endsWith('|UNROUTED')) return false;
  const hit = list.find(lc => lc.scopeKey === sk);
  return !!hit && /\(VACANT\b/.test(hit.label);
}

// Partition a set of claims into the buckets an officer's desk is ordered by.
// `opts` exists so the suite can pin `now` and inject a roster; production passes nothing.
export function partitionDesk(claims, opts) {
  const o = opts || {};
  const limit = typeof o.urgentDays === 'number' ? o.urgentDays : WINDOW_URGENT_DAYS;
  const nowMs = typeof o.nowMs === 'number' ? o.nowMs : Date.now();
  const roster = o.roster || LC_ROSTER;
  const list = Array.isArray(claims) ? claims : [];

  const withClock = list.map(c => ({
    claim: c,
    left: daysUntilISO(c && (c.expiresAt || c.windowDate), nowMs),   // windowDate: section 488's returned-claim rows
    age: daysSince(claimFiledAtSeconds(c), nowMs),
  }));

  // Expired is kept apart from closing. They are different facts needing different words: a window
  // that has run out is not a window that is nearly out, and merging them would bury the first.
  const expired = withClock.filter(x => x.left !== null && x.left < 0);
  const closing = withClock
    .filter(x => x.left !== null && x.left >= 0 && x.left <= limit)
    .sort((a, b) => a.left - b.left);

  const awaiting = withClock
    .filter(x => AWAITING_LC.includes(String((x.claim && x.claim.status) || '')))
    .sort((a, b) => {
      // Soonest deadline first. A claim with NO computable deadline sorts AFTER every claim that has
      // one, then by age, oldest first. It must never appear urgent and must never appear safe.
      if (a.left === null && b.left === null) return (b.age || 0) - (a.age || 0);
      if (a.left === null) return 1;
      if (b.left === null) return -1;
      return a.left - b.left;
    });

  const elevated = withClock
    .filter(x => ELEVATED_LC.includes(String((x.claim && x.claim.status) || '')))
    .sort((a, b) => {
      // Same unknown-last rule as `awaiting`. A claim sitting at GC Review is still burning its
      // filing window, and one with no computable window must not look like the calmest row.
      if (a.left === null && b.left === null) return (b.age || 0) - (a.age || 0);
      if (a.left === null) return 1;
      if (b.left === null) return -1;
      return a.left - b.left;
    });

  const pastDesk = withClock.filter(x => PAST_LC.includes(String((x.claim && x.claim.status) || '')));

  // Ruling 72. The officer keeps the claim in his queue while its author holds it - the return does
  // not take it off his desk, because the clock does not pause (67.2). Oldest return first: a claim
  // on its third week back is the one nobody is holding.
  const returned = withClock
    .filter(x => isReturned(x.claim))
    .sort((a, b) => String((b.claim && b.claim.returnedAt) || '') < String((a.claim && a.claim.returnedAt) || '') ? 1 : -1);
  const unrouted = withClock.filter(x => isUnrouted(x.claim));
  const unheld = withClock.filter(x => isUnheldSeat(x.claim, roster));

  // Claims carrying no computable filing deadline at all. This is the readiness surface's
  // "agreements with no filing-window profile" arriving on a real claim: the member is shown no
  // clock because the platform has none. Listed rather than left blank.
  const noClock = withClock.filter(x => x.left === null);

  return { withClock, expired, closing, awaiting, elevated, pastDesk, returned, unrouted, unheld, noClock };
}
